// React
import React from 'react';

// Components
import { MpsButton } from '@mobilitystore/components-interface';
import { CDN_LINK } from '../../assets/img';

// Statuses that are final and should render as an inert badge rather than an action button.
const TERMINAL_STATUSES = ['GRANTED', 'ACTIVE', 'CLOSED'];
const CHIP_MODIFIER_BY_STATUS = { GRANTED: 'success', ACTIVE: 'success', CLOSED: 'neutral' };
const FLEXILOANS_ORG_ID = 'FLEXILOANS';
const FLEXILOANS_LOGO_SRC = `${CDN_LINK}/seller/flexiloans-logo.svg`;

const formatStatusLabel = (status) => (status || '').split('_').join(' ');

const formatAmount = (amount) =>
  typeof amount === 'number' ? `₹ ${amount.toLocaleString('en-IN')}` : '--';

// Explicit DD/MM/YYYY formatting instead of toLocaleDateString(), which varies by browser locale.
const formatDate = (date) => {
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '--';

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${parsedDate.getFullYear()}`;
};

const LoanStatusBanner = ({
  application,
  requestedAmount,
  isActionLoading,
  onAction,
  loanIntentId,
  content
}) => {
  const { lender_details: lenderDetails, loan_application_status: status, created_date: createdDate, id } =
    application || {};
  const isTerminalStatus = TERMINAL_STATUSES.includes(status);
  const chipModifier = !application ? 'neutral' : CHIP_MODIFIER_BY_STATUS[status] || 'pending';
  const isFlexiLoans = lenderDetails?.org_id?.toUpperCase() === FLEXILOANS_ORG_ID;
  // Status lookup failed - fall back to the loan intent id so the user still has a reference number.
  const applicationId = id || loanIntentId;
  const canShowAction = Boolean(application ? !isTerminalStatus : loanIntentId);
  const notAvailableValue = content?.not_available_value;

  return (
    <section className='loan-status-banner' aria-labelledby='loan-status-banner-title'>
      <h2 id='loan-status-banner-title' className='loan-status-banner__title'>
        {content?.title}
      </h2>
      <p className='loan-status-banner__subtitle'>
        {isTerminalStatus ? content?.subtitle_processed : content?.subtitle_in_progress}
      </p>

      <div className='loan-status-banner__card'>
        {application && (
          <div className='loan-status-banner__row'>
            <span className='loan-status-banner__label'>{content?.lender_label}</span>
            <span className='loan-status-banner__value'>
              {isFlexiLoans ? (
                <img
                  className='loan-status-banner__lender-logo'
                  src={FLEXILOANS_LOGO_SRC}
                  alt={lenderDetails?.name || 'FlexiLoans'}
                />
              ) : (
                lenderDetails?.name || notAvailableValue
              )}
            </span>
          </div>
        )}

        <div className='loan-status-banner__row'>
          <span className='loan-status-banner__label'>{content?.application_id_label}</span>
          <span className='loan-status-banner__value'>{applicationId || notAvailableValue}</span>
        </div>

        {application && (
          <div className='loan-status-banner__row'>
            <span className='loan-status-banner__label'>{content?.requested_amount_label}</span>
            <span className='loan-status-banner__value'>{formatAmount(requestedAmount)}</span>
          </div>
        )}

        {createdDate && (
          <div className='loan-status-banner__row'>
            <span className='loan-status-banner__label'>{content?.applied_on_label}</span>
            <span className='loan-status-banner__value'>{formatDate(createdDate)}</span>
          </div>
        )}

        {application && (
          <div className='loan-status-banner__row'>
            <span className='loan-status-banner__label'>{content?.status_label}</span>
            <span className={`loan-status-banner__chip loan-status-banner__chip--${chipModifier}`}>
              {formatStatusLabel(status)}
            </span>
          </div>
        )}
      </div>

      {canShowAction && (
        <MpsButton
          className='loan-status-banner__action'
          label={isActionLoading ? content?.action_button_loading : content?.action_button}
          mode='primary'
          disabled={isActionLoading}
          onClick={onAction}
        />
      )}
    </section>
  );
};

export default LoanStatusBanner;
